import React, { useState, useEffect, useRef, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "./TypeaheadSelect.css";
import "./ControlPanel.css";

export interface ClusterOption {
  value: string;
  keyword: string;
  languageCode: string;
  countryIsoCode: string | null;
}

interface TypeaheadSelectProps {
  options: ClusterOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
}

const TypeaheadSelect: React.FC<TypeaheadSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Type to search...",
  id,
  className = "",
}) => {
  const selectedOption = options.find((o) => o.value === value) ?? null;
  const [inputValue, setInputValue] = useState(selectedOption?.keyword ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Sort alphabetically by keyword, then filter on the keyword column only
  const filteredOptions = useMemo(() => {
    const sorted = [...options].sort((a, b) =>
      a.keyword.toLowerCase().localeCompare(b.keyword.toLowerCase())
    );

    if (!inputValue.trim()) {
      return sorted;
    }

    return sorted.filter((option) =>
      option.keyword.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [options, inputValue]);

  // Keep input text in sync with the selected option's keyword
  useEffect(() => {
    if (!isOpen) {
      setInputValue(selectedOption?.keyword ?? "");
    }
  }, [value, isOpen, selectedOption]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    setFocusedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setFocusedIndex(-1);
  };

  const handleOptionSelect = (option: ClusterOption) => {
    setInputValue(option.keyword);
    setIsOpen(false);
    setFocusedIndex(-1);
    onChange(option.value);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue("");
    setIsOpen(false);
    setFocusedIndex(-1);
    onChange("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleOptionSelect(filteredOptions[focusedIndex]);
        } else if (filteredOptions.length === 1) {
          handleOptionSelect(filteredOptions[0]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        setInputValue(selectedOption?.keyword ?? "");
        inputRef.current?.blur();
        break;
      case "Tab":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
        // Reset input value to selected value if user clicks outside
        if (value) {
          setInputValue(selectedOption?.keyword ?? "");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, selectedOption]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[
        focusedIndex
      ] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [focusedIndex]);

  return (
    <div
      className="p-5 relative"
      style={{
        border: "4px solid transparent",
        backgroundImage:
          "linear-gradient(#F3F4FF, #F3F4FF), linear-gradient(92.69deg, #8642C7 4.57%, #8A96FF 100%)",
        borderRadius: "20px",
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <img src="/icons/anchor-kw.svg" alt="" />
          <span className="text-neutral-400 font-semibold text-sm">Anchor Keyword</span>
        </div>

        {value && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear selection"
            title="Clear selection"
          >
            <CloseIcon sx={{ fontSize: 16, color: "#acacac" }} />
          </button>
        )}
      </div>

      <div className={`typeahead-select ${className}`} ref={dropdownRef}>
        <div className="typeahead-input-container">
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="typeahead-input"
            autoComplete="off"
          />
        </div>
      </div>

      {isOpen && (
        <div className="typeahead-dropdown shadow-md">
          {filteredOptions.length > 0 ? (
            <>
              <ul ref={listRef} className="typeahead-list">
                {filteredOptions.map((option, index) => (
                  <li
                    key={option.value}
                    className={`typeahead-option typeahead-option-grid ${
                      index === focusedIndex ? "focused" : ""
                    } ${option.value === value ? "selected" : ""}`}
                    onMouseDown={() => handleOptionSelect(option)}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <span className="typeahead-col-keyword">{option.keyword}</span>
                    <span className="typeahead-col-meta">
                      {option.languageCode.toUpperCase()}
                    </span>
                    <span className="typeahead-col-meta">
                      {option.countryIsoCode ?? "-"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="typeahead-no-results">
              No keywords found matching "{inputValue}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TypeaheadSelect;
